/**
 * MinhaMesaInbox — bloco de novas demandas no topo da lista.
 *
 * Exibe notificações não lidas relevantes para "Minha Mesa":
 * task_assigned, task_transferred, task_escalated, task_returned,
 * action_assigned, stage_entered.
 *
 * Ao clicar em um item: marca como lido, navega/seleciona a tarefa.
 * "Marcar todas como lidas" limpa o bloco.
 */
import React, { useMemo, useCallback } from 'react';
import { Bell, CheckCheck, ArrowRight, AlertTriangle, UserPlus, Zap } from 'lucide-react';
import type { AppNotification } from '../../../hooks/useNotifications';

const INBOX_TYPES = new Set([
  'task_assigned',
  'task_transferred',
  'task_escalated',
  'task_returned',
  'action_assigned',
  'stage_entered',
  'problem_assigned',
]);

function getInboxIcon(type: string) {
  switch (type) {
    case 'task_escalated':
      return <AlertTriangle size={14} className="shrink-0 text-red-500 dark:text-red-400" />;
    case 'task_assigned':
    case 'stage_entered':
      return <UserPlus size={14} className="shrink-0 text-blue-500 dark:text-blue-400" />;
    case 'action_assigned':
      return <Zap size={14} className="shrink-0 text-amber-500 dark:text-amber-400" />;
    default:
      return <ArrowRight size={14} className="shrink-0 text-slate-400" />;
  }
}

function getInboxColor(type: string): string {
  switch (type) {
    case 'task_escalated':
      return 'border-l-red-500';
    case 'task_assigned':
    case 'stage_entered':
      return 'border-l-blue-500';
    case 'action_assigned':
    case 'problem_assigned':
      return 'border-l-amber-500';
    default:
      return 'border-l-slate-400';
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

interface MinhaMesaInboxProps {
  notifications: AppNotification[];
  onAcknowledge: (id: string) => void;
  onAcknowledgeAll: () => void;
  onSelectTask?: (taskId: string) => void;
}

export const MinhaMesaInbox: React.FC<MinhaMesaInboxProps> = ({
  notifications,
  onAcknowledge,
  onAcknowledgeAll,
  onSelectTask,
}) => {
  const inboxItems = useMemo(
    () => notifications
      .filter((n) => !n.read && INBOX_TYPES.has(n.type))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [notifications],
  );

  const handleClick = useCallback(
    (item: AppNotification) => {
      onAcknowledge(item.id);
      const taskId = (item.metadata as Record<string, unknown>)?.taskId as string | undefined;
      if (taskId && onSelectTask) {
        onSelectTask(taskId);
      }
    },
    [onAcknowledge, onSelectTask],
  );

  if (inboxItems.length === 0) return null;

  return (
    <div className="mx-[var(--module-page-padding)] mb-3 rounded border border-blue-200 bg-blue-50/60 dark:border-blue-900/40 dark:bg-blue-950/20">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-blue-100 dark:border-blue-900/30">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-blue-600 dark:text-blue-400" />
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-blue-800 dark:text-blue-300">
            Novas atribuições
          </span>
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-sm bg-blue-600 px-1.5 font-mono text-[10px] font-bold tabular-nums text-white dark:bg-blue-500">
            {inboxItems.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onAcknowledgeAll}
          className="flex items-center gap-1 rounded px-2 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-blue-600 transition hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30"
        >
          <CheckCheck size={12} />
          Marcar todas
        </button>
      </div>

      {/* Items */}
      <div className="max-h-[200px] overflow-y-auto divide-y divide-blue-100 dark:divide-blue-900/30">
        {inboxItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleClick(item)}
            className={`flex w-full items-start gap-3 border-l-[3px] px-4 py-2.5 text-left transition hover:bg-blue-100/60 dark:hover:bg-blue-900/20 ${getInboxColor(item.type)}`}
          >
            <div className="mt-0.5">{getInboxIcon(item.type)}</div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] font-semibold leading-snug enterprise-text-primary truncate">
                {item.title}
              </p>
              {item.message && (
                <p className="mt-0.5 font-mono text-[10px] leading-snug enterprise-text-muted truncate">
                  {item.message}
                </p>
              )}
            </div>
            <span className="shrink-0 font-mono text-[9px] tabular-nums enterprise-text-muted mt-0.5">
              {timeAgo(item.created_at)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Returns count of unread inbox-relevant notifications */
export function getInboxCount(notifications: AppNotification[]): number {
  return notifications.filter((n) => !n.read && INBOX_TYPES.has(n.type)).length;
}
